import React, { memo } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { OfferFormValues } from "../../schemas/offerFormSchema";
import { CampLocation } from "@/types/campLocation";
import { Offer } from "@/types/offer";
import { FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { FieldOwnershipBadge } from "@/components/common/FieldOwnershipBadge";
import { FieldVendorHint } from "@/components/common/FieldVendorHint";
import { Input } from "@/components/ui/input";
import { DollarSign } from "lucide-react";
import { 
  BasicInformationSection, 
  StatusSection, 
  DateRangeSection, 
  PricingAndTimingCard,
  RegistrationEventsSection 
} from "./form-sections";
import { SessionAgeOverrideSection } from "./form-sections/SessionAgeOverrideSection";
import { ProgramWebsiteReadOnly } from "@/components/admin/locations/components/ProgramLinkReadOnly";
import { sessionLinkDefaults } from "@/functions-shared/offer/sessionLinkDefaults";

interface FormSectionsProps {
  form: UseFormReturn<OfferFormValues>;
  location: CampLocation;
  markFieldAsModified: (fieldName: string) => void;
  isFieldModified: (fieldName: string) => boolean;
  resetFieldToProgram: (fieldName: string) => void;
  availabilityStatusOptions: any[];
  signupStatusOptions: any[];
  isAdmin?: boolean;
  hasInitialOffer?: boolean;
  /** The session being edited, for its own stored length (an existing session only). */
  initialOffer?: Offer | null;
  /** The offer's stored `programUrl` (an existing session only; may be pipeline-written). */
  storedProgramUrl?: string | null;
}

export const FormSections: React.FC<FormSectionsProps> = memo(({
  form,
  location,
  markFieldAsModified,
  isFieldModified,
  resetFieldToProgram,
  availabilityStatusOptions,
  signupStatusOptions,
  isAdmin = false,
  hasInitialOffer = false,
  initialOffer = null,
  storedProgramUrl = null
}) => {
  // Watch variation live so dependent sections receive up-to-date data
  const liveVariation = useWatch({ control: form.control, name: "variation" });
  const allowAgeOverride = !!liveVariation?.allowSessionAgeOverride;
  const hasTieredPricing =
    liveVariation?.pricingTiers?.numTiers === 2 || liveVariation?.pricingTiers?.numTiers === 3;
  return (
    <div className="space-y-6">
      {/* Two-column layout for basic info and status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div>
          {/* Basic Information Section - Program */}
          <BasicInformationSection 
            form={form} 
            location={location} 
            markFieldAsModified={markFieldAsModified}
            isAdmin={isAdmin}
          />
        </div>
        
        {/* Right Column */}
        <div>
          {/* Status Section */}
          <StatusSection form={form} availabilityStatusOptions={availabilityStatusOptions} signupStatusOptions={signupStatusOptions} markFieldAsModified={markFieldAsModified} />
        </div>
      </div>
      
      {/* Full-width section for date range picker */}
      <DateRangeSection form={form} markFieldAsModified={markFieldAsModified} />
      
      {/* Description field */}
      <FormField 
        control={form.control} 
        name="description" 
        render={({ field }) => (
          <div>
            <div className="flex items-center gap-2">
              <FormLabel>Session description</FormLabel>
              <FieldOwnershipBadge fieldPath="/description" />
            </div>
            <Textarea
              {...field}
              placeholder=""
              rows={3}
              maxLength={2000}
              onChange={e => {
                field.onChange(e.target.value);
                markFieldAsModified("description");
              }}
            />
            <FieldVendorHint fieldPath="/description" onUseVendorValue={(v) => { form.setValue("description", v as string); markFieldAsModified("description"); }} />
            <div className="text-xs text-muted-foreground text-right mt-1">
              {field.value?.length || 0}/2000
            </div>
          </div>
        )}
      />
      
      {/* 1. Session website */}
      <FormField
        control={form.control}
        name="url"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-2">
              <FormLabel>Session website</FormLabel>
              <FieldOwnershipBadge fieldPath="/url" />
            </div>
            <Input
              {...field}
              type="url"
              placeholder="https://example.com/register"
              onChange={e => {
                field.onChange(e.target.value);
                markFieldAsModified("url");
              }}
            />
            <FieldVendorHint fieldPath="/url" onUseVendorValue={(v) => { form.setValue("url", v as string); markFieldAsModified("url"); }} />
            <p className="text-sm text-muted-foreground mt-1">
              This session's own page. Defaults to the program's default session website.
            </p>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Program website — read-only, copied from the program on save */}
      <ProgramWebsiteReadOnly
        programUrl={sessionLinkDefaults(liveVariation).programUrl}
        storedProgramUrl={storedProgramUrl}
      />

      {/* 2. Registration */}
      <FormField
        control={form.control}
        name="regUrl"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-2">
              <FormLabel>Registration</FormLabel>
              <FieldOwnershipBadge fieldPath="/regUrl" />
            </div>
            <Input
              {...field}
              type="url"
              placeholder="https://example.com/register-here"
              onChange={e => {
                field.onChange(e.target.value);
                markFieldAsModified("regUrl");
              }}
            />
            <FieldVendorHint fieldPath="/regUrl" onUseVendorValue={(v) => { form.setValue("regUrl", v as string); markFieldAsModified("regUrl"); }} />
            <p className="text-sm text-muted-foreground mt-1">
              Where a parent signs up. With no link here, the session shows no Register button.
            </p>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* 3. Registration dates (optional) */}
      <RegistrationEventsSection 
        form={form} 
        markFieldAsModified={markFieldAsModified} 
      />

      {/* 4. Override program age range */}
      <SessionAgeOverrideSection 
        form={form} 
        variation={liveVariation as any} 
        allowOverride={allowAgeOverride} 
        resetFieldToProgram={resetFieldToProgram}
      />

      {/* 5. Eligibility notes */}
      <FormField 
        control={form.control} 
        name="eligibilityNotes" 
        render={({ field }) => (
          <div>
            <div className="flex items-center gap-2">
              <FormLabel>Eligibility notes</FormLabel>
              <FieldOwnershipBadge fieldPath="/eligibilityNotes" />
            </div>
            <Textarea
              {...field}
              placeholder=""
              rows={2}
              onChange={e => {
                field.onChange(e.target.value);
                markFieldAsModified("eligibilityNotes");
              }}
            />
            <FieldVendorHint fieldPath="/eligibilityNotes" onUseVendorValue={(v) => { form.setValue("eligibilityNotes", v as string); markFieldAsModified("eligibilityNotes"); }} />
          </div>
        )}
      />
      
      {/* Combined Pricing and Timing Card */}
      <PricingAndTimingCard 
        form={form} 
        markFieldAsModified={markFieldAsModified} 
        isFieldModified={isFieldModified} 
        resetFieldToProgram={resetFieldToProgram} 
        hasInitialOffer={hasInitialOffer}
        initialOffer={initialOffer}
      />
      
      {/* 7. Current sale price */}
      <FormField 
        control={form.control} 
        name="currentSalePrice" 
        render={({ field }) => (
          <FormItem className="max-w-[25%]">
            <FormLabel>{hasTieredPricing ? "Tier 1 current sale price (excluding add-ons and discounts)" : "Current sale price (excluding add-ons and discounts)"}</FormLabel>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <DollarSign className="h-4 w-4 text-gray-500" />
              </div>
              <Input 
                type="number" 
                value={field.value === null || field.value === undefined ? "" : field.value} 
                onChange={e => {
                  field.onChange(e.target.value ? Number(e.target.value) : null);
                  markFieldAsModified("currentSalePrice");
                }} 
                placeholder="" 
                min="0" 
                step="1"
                className="pl-9" 
              />
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
});

FormSections.displayName = "FormSections";
